import React from "react";
import { MapPin, Zap, ArrowRight, ShieldCheck, Landmark, Truck, Leaf, Users, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
const coveredTowns = ["Kanata", "Orleans", "Barrhaven", "Nepean", "Gloucester", "Stittsville", "Carp", "Rockland", "Manotick", "Glebe"];
const differentiators = [{
  label: "Locally Owned",
  icon: Landmark
}, {
  label: "Fully Insured",
  icon: ShieldCheck
}, {
  label: "Same-Day Available",
  icon: Zap
}, {
  label: "Eco-Friendly",
  icon: Leaf
}, {
  label: "Friendly Local Crew",
  icon: Users
}];
const serviceImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_team_truck_1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_team_truck_1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_team_truck_1.png-wvc-sizes",
  alt: "JUNKR team with professional truck in an Ottawa neighborhood"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/kanata_residential_cleanup.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/kanata_residential_cleanup.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/kanata_residential_cleanup.png-wvc-sizes",
  alt: "Professional junk removal service in Kanata"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_commercial_disposal.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_commercial_disposal.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/nepean_commercial_disposal.png-wvc-sizes",
  alt: "Commercial cleanout team in Nepean"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_eco_friendly_sorting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_eco_friendly_sorting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_eco_friendly_sorting.png-wvc-sizes",
  alt: "Eco-friendly disposal and recycling in Ottawa"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/barrhaven_furniture_removal.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/barrhaven_furniture_removal.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/barrhaven_furniture_removal.png-wvc-sizes",
  alt: "Local junk removal team in Barrhaven"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/stittsville_cleanout_crew.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/stittsville_cleanout_crew.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/stittsville_cleanout_crew.png-wvc-sizes",
  alt: "Friendly JUNKR crew performing a cleanout in Stittsville"
}];
export default function ServingTheHeartOfLeicestershire() {
  return <section data-section-id="236472" id="the-ottawa-service-map" className="relative overflow-hidden bg-background py-24 sm:py-32">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-10" />

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-16 items-center">
          
          <div className="relative w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
            <Carousel className="w-full">
              <CarouselContent>
                {serviceImages.map((image, i) => <CarouselItem key={i} data-index={i}>
                    <div className="relative aspect-square overflow-hidden rounded-lg border border-border bg-card shadow-xl">
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent pointer-events-none" />
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
              <div className="hidden sm:block">
                <CarouselPrevious className="left-4 bg-background/80 backdrop-blur-sm border-border hover:bg-secondary hover:text-secondary-foreground" />
                <CarouselNext className="right-4 bg-background/80 backdrop-blur-sm border-border hover:bg-secondary hover:text-secondary-foreground" />
              </div>
            </Carousel>
          </div>

          <div className="flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 text-primary w-fit mb-6 border border-primary/20">
              <Landmark className="h-4 w-4 text-secondary" />
              <span className="text-sm font-default font-semibold tracking-wide uppercase">Proudly Serving Ottawa</span>
            </div>
            
            <h2 className="font-default text-4xl font-bold tracking-tight text-primary sm:text-5xl mb-6">
              Why Ottawa Homes & Businesses Trust JUNKR
            </h2>

            <div className="flex flex-wrap gap-4 mb-8">
              {differentiators.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <item.icon className="h-4 w-4 text-secondary" />
                  <span>{item.label}</span>
                </div>)}
            </div>
            
            <p className="text-lg leading-8 text-muted-foreground mb-8 font-default">
              Serving over 10 local communities across the National Capital Region, JUNKR provides professional, eco-friendly disposal solutions. Whether you're clearing out a garage in Kanata or a commercial space in Orleans, our local teams ensure your junk is handled responsibly and recycled whenever possible.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-3 text-foreground">
                <Truck className="h-5 w-5 text-secondary" />
                <span className="font-default font-bold text-lg">Full Coverage Across the City</span>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {coveredTowns.map((town, i) => <div key={town} data-index={i}>
                    <Badge variant="outline" className="w-full justify-start py-2 px-3 text-sm bg-card/50 backdrop-blur-sm border-border hover:border-secondary/50 transition-colors rounded-md">
                      <MapPin className="h-3 w-3 mr-1.5 text-secondary" />
                      <span className="font-default">{town}</span>
                    </Badge>
                  </div>)}
              </div>

              <div className="relative rounded-lg overflow-hidden border border-border shadow-md mt-4">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_service_area_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_service_area_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_service_area_map.png-wvc-sizes" alt="Map showing JUNKR service coverage across Greater Ottawa" className="w-full h-48 object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-accent/50 border border-accent">
                <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm font-default text-foreground leading-relaxed">
                  Locally owned since 2015 — serving Ottawa families & businesses with eco-friendly disposal.
                </p>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto group bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-md">
                <Link to="/PricingPage">
                  Get a Free Quote
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-secondary/50 hover:border-secondary text-foreground rounded-md">
                <Link to="tel:6135550123">
                  <Phone className="mr-2 h-4 w-4 text-secondary" />
                  Call/text for instant availability
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground font-default">
              Don’t see your neighborhood listed? We likely still serve your area — text us your address for instant confirmation.
            </p>
          </div>
          
        </div>
      </div>
    </section>;
}